import "./MyAccount.scss";

import * as formik from "formik";
import * as yup from "yup";

import { compressImage, formatPhone } from "../../commons/util/HallAppUtil";
import { useEffect, useMemo, useRef, useState } from "react";

import AppButton from "../../commons/buttons/AppButton";
import AppContainer from "../../commons/containers/AppContainer";
import AppInput from "../../commons/inputs/AppInput";
import AppLoading from "../../commons/AppLoading";
import ConfirmModal from "../../commons/modal/ConfirmModal";
import { Form } from "react-bootstrap";
import HallAppToast from "../../commons/HallAppToast";
import UserService from "../../auth/UserService";
import noImg from "../../assets/images/no-img.jpg";
import { useMask } from "@react-input/mask";
import { useNavigate } from "react-router-dom";

const userAPI = new UserService();

function MyAccount() {
  const [account, setAccount] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [imgLogo, setImgLogo] = useState();
  const [imageLoaded, setImageLoaded] = useState(false);

  const [showModalDelete, setShowModalDelete] = useState(false);
  const [isLoadingModalDelete, setLoadingModalDelete] = useState(false);

  const [initialValues, setInitialValues] = useState({
    name: "",
    phone: "",
    email: "",
    photo: "",
  });

  const inputFileRef = useRef(null);
  const navigate = useNavigate();

  const { Formik } = formik;

  const schema = yup.object().shape({
    name: yup.string().required("Nome é obrigatório"),
    phone: yup
      .string()
      .required("Telefone é obrigatório")
      .matches(/^\(\d{2}\)\d{4,5}-\d{4}$/, "Whatsapp deve estar no formato (99)99999-9999"),
    photo: yup.string(),
  });

  useEffect(() => {
    getMyAccountData();
  }, []);

  async function getMyAccountData() {
    try {
      setIsLoading(true);
      const response = await userAPI.me();
      const user = response.data;
      setAccount(user);
      setInitialValues({
        name: user.name,
        phone: formatPhone(user.phone),
        email: user.email,
      });
    } catch (_) {
      HallAppToast.genericError();
      setAccount(null);
    } finally {
      setIsLoading(false);
    }
  }

  const whatsMask = useMask({ mask: "(__)_____-____", replacement: { _: /\d/ } });

  const previewSrc = useMemo(() => {
    if (imgLogo) return URL.createObjectURL(imgLogo);
    return account?.photo || noImg;
  }, [imgLogo, account?.photo]);

  const update = async (values) => {
    try {
      const data = { ...values };

      const formData = new FormData();
      formData.append("userDTO", new Blob([JSON.stringify(data)], { type: "application/json" }));

      if (imgLogo !== null && imgLogo !== undefined) {
        const logoCompress = await compressImage(imgLogo);
        formData.append("img", logoCompress);
      }

      setIsLoading(true);
      await userAPI.updateMe(formData);
      HallAppToast.success();
    } catch (_) {
      HallAppToast.genericError();
      setAccount(null);
    } finally {
      setIsLoading(false);
    }
  };

  function handleChangeImgLogo(e) {
    setImgLogo(e.target.files?.[0]);
  }

  async function executeDeletion() {
    try {
      setLoadingModalDelete(true);
      await userAPI.deleteAccount(account.email);
      navigate("/login");
    } catch (_) {
      HallAppToast.genericError();
    } finally {
      setLoadingModalDelete(false);
    }
  }

  return (
    <>
      <Formik
        validationSchema={schema}
        onSubmit={update}
        enableReinitialize
        initialValues={initialValues}
      >
        {({ handleSubmit, handleChange, values, touched, errors }) =>
          isLoading ? (
            <AppLoading />
          ) : (
            <div className="myacc-page">
              <AppContainer>
                <div className="myacc-card">
                  <div className="myacc-header">
                    <div className="myacc-titleWrap">
                      <h2 className="myacc-title">Minha conta</h2>
                      <p className="myacc-subtitle">
                        Atualize seus dados e sua foto de perfil.
                      </p>
                    </div>
                  </div>

                  <Form noValidate onSubmit={handleSubmit} className="myacc-form">
                    {/* Avatar */}
                    <div className="myacc-avatarRow">
                      <div className="myacc-avatarWrap">
                        <img
                          src={previewSrc}
                          alt="Foto de perfil"
                          className={`myacc-avatar ${imageLoaded ? "loaded" : ""}`}
                          onLoad={() => setImageLoaded(true)}
                        />

                        <button
                          type="button"
                          className="myacc-avatarEdit"
                          onClick={() => inputFileRef.current?.click()}
                          aria-label="Alterar foto"
                          title="Alterar foto"
                        >
                          <i className="bi bi-camera-fill" />
                        </button>

                        <input
                          ref={inputFileRef}
                          type="file"
                          accept="image/*"
                          onChange={handleChangeImgLogo}
                          style={{ display: "none" }}
                        />
                      </div>

                      <div className="myacc-avatarInfo">
                        <div className="myacc-avatarName">{values.name || "Seu nome"}</div>
                        <div className="myacc-avatarHint">
                          Clique no ícone da câmera para trocar a foto.
                        </div>

                        <div className="myacc-avatarActions">
                          <button
                            type="button"
                            className="myacc-secondaryBtn"
                            onClick={() => inputFileRef.current?.click()}
                          >
                            Adicionar / Alterar foto
                          </button>

                          {imgLogo && (
                            <button
                              type="button"
                              className="myacc-ghostBtn"
                              onClick={() => setImgLogo(null)}
                            >
                              Remover seleção
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Campos */}
                    <div className="myacc-grid">
                      <AppInput
                        isRequired
                        errors={errors}
                        touched={touched}
                        name="name"
                        onChange={handleChange}
                        value={values.name}
                        type="text"
                        label="Nome"
                      />

                      <AppInput
                        isRequired
                        errors={errors}
                        touched={touched}
                        name="phone"
                        onChange={handleChange}
                        value={values.phone}
                        mask={whatsMask}
                        type="text"
                        label="Whatsapp"
                      />

                      <AppInput
                        errors={errors}
                        touched={touched}
                        name="email"
                        onChange={handleChange}
                        value={values.email}
                        disabled
                        type="text"
                        label="E-mail"
                      />
                    </div>

                    {/* CTA */}
                    <div className="myacc-footer">
                      <AppButton name="Salvar alterações" type="submit" width="100%" />
                    </div>

                    {/* Danger zone */}
                    <div className="myacc-danger">
                      <div className="myacc-dangerText">
                        <div className="myacc-dangerTitle">Zona de perigo</div>
                        <div className="myacc-dangerDesc">
                          Excluir sua conta remove seus dados de acesso. Esta ação não pode ser desfeita.
                        </div>
                      </div>

                      <button
                        type="button"
                        className="myacc-dangerBtn"
                        onClick={() => setShowModalDelete(true)}
                      >
                        Excluir minha conta
                      </button>
                    </div>
                  </Form>
                </div>
              </AppContainer>
            </div>
          )
        }
      </Formik>

      <ConfirmModal
        show={showModalDelete}
        title="Excluir conta"
        isLoading={isLoadingModalDelete}
        confirm={executeDeletion}
        closeModal={() => setShowModalDelete(false)}
      >
        Tem certeza que deseja excluir sua conta? Essa ação não poderá ser desfeita.
      </ConfirmModal>
    </>
  );
}

export default MyAccount;
